import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Plus, Minus, X, ShoppingCart, ChefHat, ChevronLeft, ChevronRight, CopyPlus, Wand2, LayoutGrid, Users, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, recipeImage } from "@/lib/api";
import { weekRange, todayStr, addDays } from "@/lib/dates";
import AddMealDialog from "@/components/AddMealDialog";
import AutofillDialog from "@/components/AutofillDialog";
import TemplatesDialog from "@/components/TemplatesDialog";

export default function Week() {
  const navigate = useNavigate();
  const [meals, setMeals] = useState([]);
  const [dlg, setDlg] = useState({ open: false, date: null, slot: null });
  const [generating, setGenerating] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [autofillOpen, setAutofillOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [editingServings, setEditingServings] = useState(null);

  const base = addDays(new Date(), offset * 7);
  const { start, end, days } = weekRange(base);

  const load = () => { api.get(`/meals?start=${start}&end=${end}`).then((r) => setMeals(r.data)); };
  useEffect(() => { load(); }, [offset]); // eslint-disable-line

  const mealFor = (date, slot) => meals.find((m) => m.date === date && m.slot === slot);

  const removeMeal = async (id) => { await api.delete(`/meals/${id}`); toast.success("Repas retiré"); load(); };

  const changeServings = async (meal, delta) => {
    const next = Math.max(1, (meal.servings || 2) + delta);
    if (next === meal.servings) return;
    setMealServings(meal, next);
  };

  const setMealServings = async (meal, value) => {
    const next = Math.max(1, Math.min(50, value));
    if (next === meal.servings) return;
    setMeals((prev) => prev.map((m) => (m.id === meal.id ? { ...m, servings: next } : m)));
    try {
      await api.patch(`/meals/${meal.id}`, { servings: next });
    } catch {
      toast.error("Erreur");
      load();
    }
  };

  const doubleMeal = (meal) => {
    setMealServings(meal, (meal.servings || 2) * 2);
    toast.success(`En double : ${(meal.servings || 2) * 2} pers. (congelez le reste)`);
  };

  const weekTitle = offset === 0 ? "Cette semaine" : offset === 1 ? "Semaine prochaine" : offset === -1 ? "Semaine dernière" : `${days[0].dayNum} ${days[0].month} – ${days[6].dayNum} ${days[6].month}`;

  const planned = meals.filter((m) => m.recipe);
  const totalSlots = 14;
  const filled = planned.length;
  const emptySlots = totalSlots - filled;
  const midis = planned.filter((m) => m.slot === "midi").length;
  const soirs = planned.filter((m) => m.slot === "soir").length;
  const totalPersons = planned.reduce((sum, m) => sum + (m.servings || 0), 0);

  const duplicatePrev = async () => {
    setDuplicating(true);
    try {
      const prev = weekRange(addDays(base, -7));
      const res = await api.get(`/meals?start=${prev.start}&end=${prev.end}`);
      const prevMeals = res.data;
      if (!prevMeals.length) { toast.error("La semaine précédente est vide"); return; }
      let count = 0;
      for (const m of prevMeals) {
        const idx = prev.days.findIndex((d) => d.date === m.date);
        if (idx < 0) continue;
        const target = days[idx].date;
        if (meals.some((x) => x.date === target && x.slot === m.slot)) continue;
        await api.post("/meals", { recipe_id: m.recipe_id, date: target, slot: m.slot, servings: m.servings });
        count++;
      }
      toast.success(count ? `${count} repas copiés` : "Créneaux déjà remplis");
      load();
    } catch {
      toast.error("Erreur lors de la copie");
    } finally {
      setDuplicating(false);
    }
  };

  const generateList = async () => {    setGenerating(true);
    try {
      await api.post("/shopping-list/generate", { start, end, exclude_pantry: true });
      toast.success("Liste de courses générée !");
      navigate("/shopping");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ajoutez d'abord des repas");
    } finally {
      setGenerating(false);
    }
  };

  const Slot = ({ date, slot, icon: Icon, color }) => {
    const m = mealFor(date, slot);
    return (
      <div className="flex-1">
        {m && m.recipe ? (
          <div className="relative overflow-hidden rounded-2xl border border-black/5 bg-white at-shadow" data-testid={`slot-${date}-${slot}`}>
            <button onClick={() => navigate(`/recipes/${m.recipe.id}`)} className="flex w-full items-center gap-2 p-2 text-left at-press">
              <div className="h-11 w-11 shrink-0 overflow-hidden rounded-xl" style={{ background: "var(--at-elevated)" }}>
                {recipeImage(m.recipe) ? <img src={recipeImage(m.recipe)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><ChefHat size={16} style={{ color: "var(--at-muted)" }} /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase" style={{ color }}><Icon size={11} /> {slot}</div>
                <p className="truncate text-sm font-medium leading-tight">{m.recipe.title}</p>
              </div>
            </button>
            {editingServings === m.id ? (
              <div className="flex items-center gap-2 border-t border-black/5 px-2 py-1.5" data-testid={`servings-editor-${m.id}`}>
                <button onClick={() => changeServings(m, -1)} data-testid={`servings-minus-${m.id}`}
                  className="flex h-6 w-6 items-center justify-center rounded-full at-press" style={{ background: "var(--at-elevated)" }}><Minus size={13} /></button>
                <span className="min-w-[52px] text-center text-xs font-semibold" data-testid={`servings-value-${m.id}`}>{m.servings} pers.</span>
                <button onClick={() => changeServings(m, 1)} data-testid={`servings-plus-${m.id}`}
                  className="flex h-6 w-6 items-center justify-center rounded-full at-press" style={{ background: "var(--at-elevated)" }}><Plus size={13} /></button>
                <button onClick={() => setEditingServings(null)} data-testid={`servings-done-${m.id}`}
                  className="ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ color: "var(--at-brand)" }}>OK</button>
              </div>
            ) : (
              <div className="flex items-center border-t border-black/5">
                <button onClick={() => setEditingServings(m.id)} data-testid={`servings-btn-${m.id}`}
                  className="flex-1 px-2 py-1 text-left text-[11px] at-press" style={{ color: "var(--at-muted)" }}>
                  {m.servings} pers.
                </button>
                <button onClick={() => doubleMeal(m)} data-testid={`double-meal-${m.id}`}
                  className="mr-1 rounded-full px-2 py-0.5 text-[10px] font-bold at-press"
                  style={{ background: "var(--at-elevated)", color: "var(--at-brand)" }}
                  title="Cuisiner en double (congeler le reste)">×2</button>
              </div>
            )}
            <button onClick={() => removeMeal(m.id)} data-testid={`remove-meal-${m.id}`} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/10 text-black/50"><X size={13} /></button>
          </div>
        ) : (
          <button data-testid={`add-slot-${date}-${slot}`} onClick={() => setDlg({ open: true, date, slot })}
            className="flex w-full items-center justify-center gap-1 rounded-2xl border border-dashed py-3.5 at-press"
            style={{ borderColor: "var(--at-border)", color: "var(--at-muted)" }}>
            <Icon size={13} /> <Plus size={13} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="px-5 pt-8" data-testid="week-page">
      <header className="at-fade-up">
        <h1 className="font-display text-3xl font-bold">Ma semaine</h1>
        <p className="mt-1 text-sm" style={{ color: "var(--at-text-2)" }}>Planifiez vos midis et soirs, puis générez votre liste.</p>
      </header>

      {/* Navigation semaine */}
      <div className="mt-4 flex items-center justify-between rounded-2xl border border-black/5 bg-white p-2 at-shadow at-fade-up" data-testid="week-nav">
        <button data-testid="prev-week-btn" onClick={() => setOffset((o) => o - 1)} className="flex h-10 w-10 items-center justify-center rounded-xl at-press" style={{ color: "var(--at-text-2)" }}><ChevronLeft size={22} /></button>
        <div className="text-center">
          <p className="font-display text-base font-semibold" data-testid="week-title">{weekTitle}</p>
          <p className="text-[11px]" style={{ color: "var(--at-muted)" }}>{days[0].dayNum} {days[0].month} – {days[6].dayNum} {days[6].month}</p>
        </div>
        <button data-testid="next-week-btn" onClick={() => setOffset((o) => o + 1)} className="flex h-10 w-10 items-center justify-center rounded-xl at-press" style={{ color: "var(--at-text-2)" }}><ChevronRight size={22} /></button>
      </div>

      {/* Résumé de la semaine */}
      <div className="mt-3 rounded-2xl border border-black/5 bg-white p-4 at-shadow at-fade-up" data-testid="week-summary">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2.5" data-testid="summary-planned">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--at-elevated)", color: "var(--at-brand)" }}><CalendarDays size={17} /></div>
            <div className="min-w-0">
              <p className="text-sm font-bold leading-none">{filled}<span className="text-xs font-medium" style={{ color: "var(--at-muted)" }}>/{totalSlots}</span></p>
              <p className="text-[11px] leading-tight" style={{ color: "var(--at-muted)" }}>repas planifiés</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5" data-testid="summary-persons">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--at-elevated)", color: "var(--at-sage)" }}><Users size={17} /></div>
            <div className="min-w-0">
              <p className="text-sm font-bold leading-none">{totalPersons}</p>
              <p className="text-[11px] leading-tight" style={{ color: "var(--at-muted)" }}>personnes servies</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5" data-testid="summary-slots">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl gap-0.5" style={{ background: "var(--at-elevated)" }}>
              <Sun size={13} style={{ color: "var(--at-honey)" }} /><Moon size={13} style={{ color: "#4A4E7A" }} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold leading-none">{midis} <span className="font-medium" style={{ color: "var(--at-muted)" }}>·</span> {soirs}</p>
              <p className="text-[11px] leading-tight" style={{ color: "var(--at-muted)" }}>midis · soirs</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5" data-testid="summary-empty">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--at-elevated)", color: "var(--at-muted)" }}><Plus size={17} /></div>
            <div className="min-w-0">
              <p className="text-sm font-bold leading-none">{emptySlots}</p>
              <p className="text-[11px] leading-tight" style={{ color: "var(--at-muted)" }}>créneaux libres</p>
            </div>
          </div>
        </div>
        {filled > 0 && (
          <Button data-testid="summary-generate-btn" onClick={generateList} disabled={generating}
            className="mt-3 h-11 w-full rounded-2xl text-sm font-semibold text-white at-press" style={{ background: "var(--at-sage)" }}>
            <ShoppingCart size={17} className="mr-2" /> {generating ? "Génération..." : "Générer ma liste de courses"}
          </Button>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        {offset !== 0 && (
          <Button data-testid="reset-week-btn" variant="outline" onClick={() => setOffset(0)} className="min-w-0 flex-1 rounded-2xl px-2">Cette semaine</Button>
        )}
        <Button data-testid="duplicate-week-btn" variant="outline" onClick={duplicatePrev} disabled={duplicating}
          className="min-w-0 flex-1 rounded-2xl px-2" style={{ borderColor: "var(--at-sage)", color: "var(--at-sage)" }}>
          <CopyPlus size={16} className="mr-1.5" /> <span className="truncate">{duplicating ? "Copie..." : "Copier semaine précédente"}</span>
        </Button>
      </div>

      <div className="mt-2 flex gap-2">
        <Button data-testid="autofill-week-btn" variant="outline" onClick={() => setAutofillOpen(true)}
          className="min-w-0 flex-1 rounded-2xl px-2" style={{ borderColor: "var(--at-honey)", color: "var(--at-honey)" }}>
          <Wand2 size={16} className="mr-1.5" /> <span className="truncate">Compléter avec des favoris</span>
        </Button>
        <Button data-testid="templates-btn" variant="outline" onClick={() => setTemplatesOpen(true)}
          className="min-w-0 flex-1 rounded-2xl px-2" style={{ borderColor: "#4A4E7A", color: "#4A4E7A" }}>
          <LayoutGrid size={16} className="mr-1.5" /> <span className="truncate">Semaines types</span>
        </Button>
      </div>

      <div className="mt-4 space-y-3">
        {days.map((d, i) => {
          const isToday = d.date === todayStr();
          return (
            <div key={d.date} className="at-fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`day-${d.date}`}>
              <div className="mb-1.5 flex items-baseline gap-2">
                <h3 className="font-display text-lg font-semibold">{d.label}</h3>
                <span className="text-xs" style={{ color: "var(--at-muted)" }}>{d.dayNum} {d.month}</span>
                {isToday && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: "var(--at-brand)" }}>Aujourd'hui</span>}
              </div>
              <div className="flex gap-2">
                <Slot date={d.date} slot="midi" icon={Sun} color="var(--at-honey)" />
                <Slot date={d.date} slot="soir" icon={Moon} color="#4A4E7A" />
              </div>
            </div>
          );
        })}
      </div>

      <Button data-testid="generate-list-btn" onClick={generateList} disabled={generating}
        className="mt-6 h-14 w-full rounded-2xl text-base font-semibold text-white at-shadow-lg at-press" style={{ background: "var(--at-sage)" }}>
        <ShoppingCart size={20} className="mr-2" /> {generating ? "Génération..." : "Générer ma liste de courses"}
      </Button>
      <p className="mt-2 text-center text-xs" style={{ color: "var(--at-muted)" }}>
        Une fois créée, votre liste se met à jour automatiquement quand vous ajoutez ou retirez une recette de cette semaine.
      </p>

      <AddMealDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))}
        fixedDate={dlg.date} fixedSlot={dlg.slot} onAdded={load} />
      <AutofillDialog open={autofillOpen} onOpenChange={setAutofillOpen} start={start} end={end} onDone={load} />
      <TemplatesDialog open={templatesOpen} onOpenChange={setTemplatesOpen} start={start} end={end} onApplied={load} />
    </div>
  );
}
